import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { STATUS_COLORS } from '@/utils/constants';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface SalesByStatusChartProps {
  chartData: Array<{
    status: string;
    title: string;
    value: number;
  }>;
  dateRange?: string;
  isLoading?: boolean;
}

export function SalesByStatusChart({
  chartData,
  dateRange,
  isLoading,
}: SalesByStatusChartProps) {
  const { t } = useTranslation('common');

  const maxValue = Math.max(...chartData.map((item) => item.value), 1);

  return (
    <Card className="overflow-hidden">
      <div className="px-4 py-4 border-b border-colorBorder">
        <h3 className="text-lg font-semibold text-colorText">
          {t('qt_sales')}
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">{dateRange}</p>
      </div>

      <div className="p-4">
        {isLoading ? (
          <Skeleton className="w-full h-[300px]" />
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" horizontal />
              <XAxis
                type="number"
                tick={{ fill: '#a1a1aa', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                dataKey="title"
                type="category"
                tick={{ fill: '#a1a1aa', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                width={140}
              />
              <RechartsTooltip
                cursor={{ fill: 'rgba(169, 211, 66, 0.1)' }}
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null;
                  const data = payload[0].payload;
                  return (
                    <div className="bg-zinc-900 border border-colorBorder px-3 py-2 rounded-lg shadow-xl">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: STATUS_COLORS[data.status] || '#A9D342' }}
                        />
                        <span className="text-muted-foreground text-xs">
                          {data.title}:
                        </span>
                        <span className="text-colorText font-medium text-xs">
                          {data.value}
                        </span>
                      </div>
                    </div>
                  );
                }}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={32}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={STATUS_COLORS[entry.status] || '#A9D342'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}
